import defaultProfile, { asyncRoute } from "./default";
import { type ConfigSection, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";

import { faChartLine, faWandMagicSparkles } from "@fortawesome/pro-solid-svg-icons";
import { commonMenuItems } from "./common";
import { i18n } from "@/i18n";
import { faPuzzlePiece } from "@fortawesome/free-solid-svg-icons";
import { computed, watch } from "vue";
import { useServiceStore } from "@/store/service";
import type { IMod, IUseMods } from "@/components/views/mods";
import { useMinecraftMods } from "@/components/views/mods/minecraft";
import { usePropertyBuilder } from "@/components/views/config/helpers";
const { t } = i18n.global;

const createMenu = (ctx: ProfileCtx): Menu => {
  const settingsMenuSection = {
    name: "settings",
    items: [
      new MenuItem(
        "",
        faChartLine,
        asyncRoute(() => import("../components/views/status/minecraft/MinecraftStatus.vue")),
      ),
      commonMenuItems.settings,
      commonMenuItems.fileManager,
      commonMenuItems.console,
      new MenuItem(
        "region-fixer",
        faWandMagicSparkles,
        asyncRoute(() => import("../components/views/config/minecraft/MinecraftRegionFixer.vue")),
      ),
      new MenuItem(
        "mods",
        faPuzzlePiece,
        asyncRoute(() => import("../components/views/mods/minecraft/ModsDashboard.vue")),
        [
          {
            route: "manage",
            component: asyncRoute(() => import("../components/views/mods/manage/ModsManage.vue")),
          },
        ],
      ),
    ],
  };

  return [settingsMenuSection, ...defaultProfile(ctx).menu.slice(1)];
};

const configSections: ConfigSection[] = [
  { component: "MinecraftModSelector" },
  { component: "ConfSection", props: { name: "basic" } },
  { component: "BroadcastMessages" },
  { component: "ConfSection", props: { name: "extended" } },
  { component: "MinecraftWhitelist" },
  { component: "MinecraftOps" },
  { component: "MinecraftBans" },
];

// These commands have corresponding localisation keys for their description and arguments
const consoleCommands = [
  "gamemode",
  "give",
  "kill",
  "playsound",
  "tp",
  "xp",
  "weather",
  "ban",
  "ban-ip",
  "banlist",
  "deop",
  "kick",
  "list",
  "op",
  "pardon",
  "pardon-ip",
  "save-all",
  "save-off",
  "save-on",
  "stop",
  "whitelist",
];

async function join() {
  window.open(t("ngp/settings/section/minecraft_wiki/link"), "_blank", "noopener,noreferrer");
}

function useMods() {
  const service = useServiceStore();
  const { buildProperty } = usePropertyBuilder();
  // Persist installed mod names in publicConfigs under virtual.installedMods so commitService() saves them
  const persistedInstalledMods = buildProperty<string[]>("virtual", "installedMods", []);

  // Seed once from legacy profile.installedMods when it becomes available
  const legacyInstalled = computed(() => (service.ctx.profile as any)?.installedMods ?? []);
  const watcher = { stop: undefined as ReturnType<typeof watch> | undefined };
  const seedLegacy = (legacyList: string[]) => {
    if ((!persistedInstalledMods.value || persistedInstalledMods.value.length === 0) && legacyList.length > 0) {
      persistedInstalledMods.value = [...legacyList];
      watcher.stop?.();
    }
  };
  watcher.stop = watch(legacyInstalled, seedLegacy, { immediate: true });

  const installedModNames = computed<string[]>({
    get: () => persistedInstalledMods.value ?? [],
    set: (val) => {
      persistedInstalledMods.value = val ?? [];
    },
  });
  return useMinecraftMods(installedModNames);
}

export default (ctx: ProfileCtx): GameProfile<IUseMods<IMod>> => ({
  menu: createMenu(ctx),
  configSections,
  consoleCommands,
  hasDns: true,
  isBeta: false, // one-way: WI→NGP only; no "Legacy" button in NGP
  join: {
    buttonTextKey: "ngp/settings/section/minecraft_wiki",
    joinFn: join,
  },
  useMods,
});
